import * as React from 'react';
import { useEffect, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { MenuItem, Select, Typography, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMultilingualContext } from '../components/multilingualProvider';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import ReactCountryFlag from 'react-country-flag';


export const MultilingualDropdown = () => {
  const { i18n } = useTranslation();
  const { currentLanguage, setLanguage } = useMultilingualContext();

  // Values persisted elsewhere
  const language_code = localStorage.getItem('language_code'); // = backend
  const persistedLanguage = localStorage.getItem('language'); // last applied
  const preselectedLanguage = localStorage.getItem('selected_language'); // one-time pre-login selection

  // What app should display in UI before login
  const appliedLanguage = persistedLanguage || language_code || currentLanguage || 'en';
  // What dropdown should show as the user's explicit choice (can be empty = no selection)
  const initialSelected = preselectedLanguage || '';

  const [languageValue, setLanguageValue] = useState(initialSelected);
  const {t:tTopBar} = useTranslation('topbar_config');
  const languageOption = [
    { title: 'English', value: 'en', countryCode: 'US' },
    { title: 'हिंदी', value: 'hi', countryCode: 'IN' },
    { title: 'Español', value: 'es', countryCode: 'ES' },
    { title: 'Português', value: 'pt', countryCode: 'PT' },
  ];

  interface LanguageChangeEvent {
    target: { value: string };
  }

  const handleChange = (event: LanguageChangeEvent): void => {
    const newLanguage = event.target.value;
    const supported = ['en', 'es', 'hi', 'pt'];

    if (!newLanguage) {
      // User cleared selection -> revert to appliedLanguage (persisted/backend)
      const fallback = persistedLanguage || language_code || 'en';
      i18n.changeLanguage(fallback);
      setLanguageValue('');
      setLanguage(fallback);
      localStorage.removeItem('selected_language');
      return;
    }

    const next = supported.includes(newLanguage) ? newLanguage : 'en';
    i18n.changeLanguage(next);
    setLanguageValue(next);
    setLanguage(next);
    // Do NOT set localStorage.language here we only set a one-time pre-login choice
    localStorage.setItem('selected_language', next);
  };

  useEffect(() => {
    i18n.changeLanguage(appliedLanguage);
    setLanguage(appliedLanguage);
  }, [appliedLanguage, i18n, setLanguage]);

  return (
    <Box sx={{ minWidth: 120 }} mb={1}>
      <FormControl fullWidth>
        <InputLabel id="multilingual-select-label" shrink>{tTopBar('language')}</InputLabel>
        <Select
          displayEmpty
          autoWidth
          labelId="multilingual-select-label"
          id="multilingual-select"
          label={tTopBar('language')}
          renderValue={(value) => {
            if (!value) {
              return (
                <Typography variant="body2" color="text.secondary">
                  Select language
                </Typography>
              );
            }
            const selected = languageOption.find((l) => l.value === value);
            if (!selected) return value as string;
            return (
              <Box display="flex" alignItems="center" gap={1}>
                <ReactCountryFlag
                  svg
                  countryCode={selected.countryCode}
                  style={{ width: '1.2em', height: '1.2em' }}
                  aria-label={selected.title}
                />
                <Typography variant="body2" fontWeight={600} color="secondary.main">
                  {selected.title}
                </Typography>
              </Box>
            );
          }}
          value={languageValue}
          onChange={handleChange}
          IconComponent={KeyboardArrowDownIcon}
          sx={{
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: '1px solid #E17E61',
            },
            height: 42,
            minHeight: 42,
            '.MuiSelect-select': {
              paddingTop: '6px',
              paddingBottom: '6px',
              minHeight: 'unset',
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: { maxHeight: 160 },
            },
          }}
        >
          {languageOption.map((item, index) => (
            <MenuItem key={item.value + index} value={item.value}>
              <Box display="flex" alignItems="center" gap={1}>
                <ReactCountryFlag
                  svg
                  countryCode={item.countryCode}
                  style={{ width: '1.2em', height: '1.2em' }}
                  aria-label={item.title}
                />
                <Typography
                  variant="body2"
                  color={languageValue === item.value ? 'vidaiOrange' : 'secondary.main'}
                  fontWeight="600"
                >
                  {item.title}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};
export default MultilingualDropdown;
